const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;

const YT_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]);
const SHORT_HOSTS = new Set(['youtu.be', 'www.youtu.be']);
const PATH_PREFIXES = ['embed', 'shorts', 'live', 'v', 'e'];

export function isVideoId(value: string): boolean {
  return VIDEO_ID.test(value);
}

/**
 * Extract a YouTube video id from a pasted value. Parsing only — the URL is never fetched.
 * Accepts a bare id or known YouTube URL shapes; everything else returns null.
 */
export function parseYouTubeInput(raw: string): string | null {
  const input = raw.trim();
  if (input.length === 0 || input.length > 300) return null;
  if (isVideoId(input)) return input;

  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(input) ? input : `https://${input}`);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (url.username || url.password) return null;
  if (url.port && url.port !== '443' && url.port !== '80') return null;
  const host = url.hostname.toLowerCase();

  if (SHORT_HOSTS.has(host)) {
    const id = url.pathname.split('/')[1] ?? '';
    return isVideoId(id) ? id : null;
  }
  if (!YT_HOSTS.has(host)) return null;

  const segments = url.pathname.split('/').filter(Boolean);
  if (segments[0] === 'watch') {
    const v = url.searchParams.get('v') ?? '';
    return isVideoId(v) ? v : null;
  }
  if (segments.length >= 2 && PATH_PREFIXES.includes(segments[0]!)) {
    const id = segments[1]!;
    return isVideoId(id) ? id : null;
  }
  return null;
}

/** Parse an ISO-8601 duration as returned by videos.list contentDetails.duration. */
export function parseIsoDuration(value: string | null | undefined): number | null {
  if (!value) return null;
  const m = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.exec(value);
  if (!m) return null;
  const [, d, h, min, s] = m;
  const ms =
    (Number(d ?? 0) * 86400 + Number(h ?? 0) * 3600 + Number(min ?? 0) * 60 + Number(s ?? 0)) *
    1000;
  // "P0D" is what YouTube reports for live/upcoming broadcasts: treat as unknown.
  return ms > 0 ? Math.round(ms) : null;
}
